import React, { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import {
  useProductSelector,
  useProductDispatch,
  PromoCodeAliasesActions,
} from '@wajeez-web/stores/product';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { DataTableWrapper, Header } from '@wajeez-web/components/helpers';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { ConfirmationModal } from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';

import { PromoCodeAliasModal } from '../components/PromoCodeAliasModal';

const PromoCodeAliasesPage = () => {
  const productDispatch = useProductDispatch();

  const [isPromoCodeAliasModalOpen, setIsPromoCodeAliasModalOpen] =
    useState(false);
  const [
    isChangePublicationStateConfirmationModalOpen,
    setIsChangePublicationStateConfirmationModalOpen,
  ] = useState(false);
  const [
    changePublicationStateConfirmationModalMessage,
    setChangePublicationStateConfirmationModalMessage,
  ] = useState('');
  const [selectedPromoCodeAlias, setSelectedPromoCodeAlias] =
    useState<Partial<Server.PromoCodeAliasDetailOutput>>();

  const datatable = useProductSelector(
    ({ promoCodeAliases }) => promoCodeAliases.promoCodeAliasesDatatable
  );

  const changePublicationStateStatus = useProductSelector(
    ({ promoCodeAliases }) =>
      promoCodeAliases.changePromoCodeAliasPublicationStatus.status
  );

  const { tablePreferances, setTablePreferences, isTableReferencesLoading } =
    HandleTableData(
      'promoCodeAliases',
      PromoCodeAliasesActions.getPromoCodeAliasesDatatableAsync
    );

  return (
    <div>
      <PageLayoutsContainer
        title="Promo Code Aliases"
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add Promo Code Alias'}
                bg={'primary'}
                color={'white'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsPromoCodeAliasModalOpen(true);
                }}
              />
            ),
            permission: [AdminSystemPermissions.promoCodeAliases.add],
          },
        ]}
        withoutRightPadding={true}
        breadCrumbsLinks={[{ title: 'Promo Code Aliases' }]}
      >
        <DataTableWrapper
          data={datatable.data as contentEntityDataType}
          isLoading={datatable.status === 'loading' || isTableReferencesLoading}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: Header) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          rowActions={(row) => [
            {
              title: 'Edit',
              permission: AdminSystemPermissions.promoCodeAliases.edit,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedPromoCodeAlias({
                  id: Number(row[1]),
                  published: row[4] === 'True' ? true : false,
                });
                setIsPromoCodeAliasModalOpen(true);
              },
            },
            {
              title: row[4] === 'True' ? 'Unpublish' : 'Publish',
              permission: AdminSystemPermissions.promoCodeAliases.publication,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedPromoCodeAlias({
                  id: Number(row[1]),
                  published: row[4] === 'True' ? true : false,
                });
                setIsChangePublicationStateConfirmationModalOpen(true);
                setChangePublicationStateConfirmationModalMessage(
                  `Are you sure that you want to ${
                    row[4] === 'True' ? 'unpublish' : 'publish'
                  } this promo code alias?`
                );
              },
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      {isPromoCodeAliasModalOpen && (
        <PromoCodeAliasModal
          isOpen={isPromoCodeAliasModalOpen}
          onClose={() => {
            setIsPromoCodeAliasModalOpen(false);
            setSelectedPromoCodeAlias(undefined);
          }}
          onSubmit={() => {
            setIsPromoCodeAliasModalOpen(false);

            if (selectedPromoCodeAlias?.id) {
              setSelectedPromoCodeAlias(undefined);
            }

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
          id={selectedPromoCodeAlias?.id}
        />
      )}
      {isChangePublicationStateConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isChangePublicationStateConfirmationModalOpen}
          onClose={() => {
            setIsChangePublicationStateConfirmationModalOpen(false);
            setSelectedPromoCodeAlias(undefined);
          }}
          message={changePublicationStateConfirmationModalMessage}
          onSubmit={async () => {
            if (selectedPromoCodeAlias) {
              const res = await productDispatch(
                PromoCodeAliasesActions.changePromoCodeAliasPublicationStatusAsync(
                  {
                    promoCodeAliasId: selectedPromoCodeAlias.id,
                    publish: !selectedPromoCodeAlias.published,
                  }
                )
              );

              if (res.meta.requestStatus === 'fulfilled') {
                setIsChangePublicationStateConfirmationModalOpen(false);
                setSelectedPromoCodeAlias(undefined);

                tablePreferances &&
                  setTablePreferences({
                    ...tablePreferances,
                  });
              }
            }
          }}
          isLoading={changePublicationStateStatus === 'loading'}
        />
      )}
    </div>
  );
};

export default PromoCodeAliasesPage;
